

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../auth/Auth';
import {
  Card,
  Grid,
  Modal,
  Box,
  TextField,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import colors from "assets/theme/base/colors";
import TableActionMenu from "components/TableActionMenu";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import Icon from "@mui/material/Icon";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import CustomInputForm from 'components/CustomInputForm';
import AccountFormModal from 'components/NewAccountForm';
import { formatDate } from 'utils/DateTimeConversion';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import EmptyState from 'components/EmptyTable/EmptyState';
import { usePermission } from 'auth/usePermission';
import { frontendLogger } from 'utils/logger';
import { getApiErrorMessage } from 'utils/apiErrors';
import StatusBadge from 'components/StatusBadge';
import MonoText from 'components/MonoText';
import { fetchAllCountries } from 'api/countriesNow';
import { useCurrencyCodes } from 'hooks/useCurrencyCodes';
import useOpenCreateFromUrl from "hooks/useOpenCreateFromUrl";
import { useTheme } from '@mui/material/styles';
import PageHeader from 'components/v2/PageHeader';
import { useActiveApp } from 'shell-v2/apps';

function Accounts() {
  const { currencies } = useCurrencyCodes();
  const defaultCurrency = useMemo(
    () => currencies.find(c => c.code === 'INR') || currencies[0] || null,
    [currencies]
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setfilteredAccounts] = useState([]);
  const [managers, setManagers] = useState([]); // Change this to an array
  const [managerList, setManagerList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { user, axiosInstance } = useAuth();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [currency, setCurrency] = useState(defaultCurrency);
  const { canCreate, isSupportUser, canDelete } = usePermission('accounts');
  // UI v2 layout (design-reference "Travel · Bookings list"): page header,
  // statistics row, then the table in one card.
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { app } = useActiveApp();

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    account: null,
    loading: false,
    checkingDependencies: false,
    dependencies: null,
  });
  const fetchAccounts = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/accounts/`);
      const transformedAccounts = transformAccountData(response.data);
      setAccounts(transformedAccounts);
      setfilteredAccounts(transformedAccounts);
    } catch (error) {
      console.error('Error fetching accounts data:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching accounts. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axiosInstance.get(`/users/`);
      const managersData = response.data.map(user => ({
        id: user.id,
        username: user.username,
        email: user.email,
        fullname: user.fullname,
      }));

      setManagers(managersData); // Set managers to an array of objects
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };


  const transformAccountData = (serverData) => {
    // Keep every field the API returns: the filter drawer and the column chooser
    // read them by name (billing_city, number_of_employees, cf1, ...)
    return serverData.map(account => ({
      ...account,
      partner_code: account.partner_code || '',
      account_type: account.account_type || '',
      partner_type: account.partner_type || '',
      service_type: Array.isArray(account.service_type)
        ? account.service_type.join(', ')
        : (account.service_type || ''),
      country: account.country_of_registration || account.billing_country || '',
      currency: account.currency || 'USD', // Use default currency if not provided
      // Raw timestamp: date filters and sorting need it; the Cell formats it
      updated_at: account.updated_at || account.modified_date,
    }));
  };

  useEffect(() => {
    fetchAccounts();
    fetchManagers();
  }, []);

  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const list = await fetchAllCountries(axiosInstance);
        setCountries(list || []);
      } catch (error) {
        console.error('Error fetching countries:', error);
        setCountries([]);
      }
      setLoadingCountries(false);
    };

    fetchCountries();
  }, [axiosInstance]);

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  // v2 header "New account": the same dialog as Add Account.
  useOpenCreateFromUrl(handleOpenModal);
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSaveAccount = async (formData) => {
    // Check if the user is a support user
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create accounts.',
        severity: 'error',
      });
      return; // Exit the function early
    }

    setIsLoading(true);
    handleCloseModal();

    try {
      // Create a new object with the form data and additional fields
      const createdAt = new Date().toISOString();
      const updatedAt = new Date().toISOString();

      const accountData = {
        ...formData,
      };

      // Remove any undefined fields
      Object.keys(accountData).forEach(key => {
        if (accountData[key] === undefined) delete accountData[key];
      });

      const response = await axiosInstance.post(`/accounts/`, accountData);

      // Transform and add the new account to the state
      const newAccount = transformAccountData([response.data])[0];
      setAccounts(prevAccounts => [...prevAccounts, newAccount]);
      setfilteredAccounts(prevAccounts => [...prevAccounts, newAccount]);

      // Log account creation
      await frontendLogger.logCreate('Account', {
        id: response.data.id,
        name: response.data.name,
        created_by: user.id,
        created_at: response.data.created_at || new Date().toISOString(),
        user: user?.username,
        action: 'create',
        timestamp: new Date().toISOString()
      });

      setSnackbar({
        open: true,
        message: 'Account created successfully.',
        severity: 'success',
      });
      navigate(`/accounts/${response.data.id}`);

    } catch (error) {
      console.error('Error creating new account:', error);
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, 'Error creating new account. Please try again.'),
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };



  // Navigation handler
  const handleRowClick = (rowData) => {
    navigate(`/accounts/${rowData.id}`);
  };

  const handleDeleteClick = async (account) => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to delete accounts.',
        severity: 'error',
      });
      return;
    }
    setDeleteDialog({
      open: true,
      account,
      loading: false,
      checkingDependencies: true,
      dependencies: null,
    });
    try {
      const response = await axiosInstance.get(`/accounts/${account.id}/dependencies`);
      setDeleteDialog((prev) => ({
        ...prev,
        checkingDependencies: false,
        dependencies: response.data,
      }));
    } catch (err) {
      console.error('Error checking dependencies:', err);
      setDeleteDialog((prev) => ({
        ...prev,
        checkingDependencies: false,
        dependencies: null,
      }));
    }
  };

  const handleDeleteCancel = () => {
    if (deleteDialog.loading) return;
    setDeleteDialog({
      open: false,
      account: null,
      loading: false,
      checkingDependencies: false,
      dependencies: null,
    });
  };

  const handleDeleteConfirm = async () => {
    const { account } = deleteDialog;
    if (!account) return;
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to delete accounts.',
        severity: 'error',
      });
      return;
    }

    setDeleteDialog((d) => ({ ...d, loading: true }));
    try {
      await axiosInstance.delete(`/accounts/${account.id}`);

      try {
        await frontendLogger.logDelete('Account', {
          id: account.id,
          name: account.name,
          user: user?.username,
          deleted_by: user?.id,
        });
      } catch (logError) {
        console.error('Error logging account deletion:', logError);
      }

      setAccounts((prev) => prev.filter((a) => a.id !== account.id));
      setfilteredAccounts((prev) => prev.filter((a) => a.id !== account.id));

      setDeleteDialog({
        open: false,
        account: null,
        loading: false,
        checkingDependencies: false,
        dependencies: null,
      });
      setSnackbar({
        open: true,
        message: 'Account deleted successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting account:', error);
      try {
        await frontendLogger.logError(error, {
          context: 'Account deletion',
          accountId: account.id,
          accountName: account.name,
          user: user?.username,
          action: 'delete',
          timestamp: new Date().toISOString(),
        });
      } catch (logError) {
        console.error('Error logging deletion failure:', logError);
      }

      const detail = error.response?.data?.detail;
      setSnackbar({
        open: true,
        message: typeof detail === 'string' ? detail : 'Error deleting account. Please try again.',
        severity: error.response?.status === 400 ? 'warning' : 'error',
      });
      setDeleteDialog((d) => ({ ...d, loading: false }));
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const capitalizeFirstLetter = (str) => {
    if (!str) return '';
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Update the accountsTableData
  const accountsTableData = useMemo(() => ({
    columns: [
      {
        Header: "ID",
        accessor: "id",
        width: "5%",
        Cell: ({ row }) => (
          <MDTypography
            component="span"
            onClick={() => handleRowClick(row.original)}
            variant="caption"
            color="info"
            fontWeight="medium"
            sx={{ cursor: 'pointer' }}
          >
            {`#${row.original.id}`}
          </MDTypography>
        ),
      },
      {
        Header: "Name",
        accessor: (row) => `${row.name || ''} ${row.partner_code || ''}`.trim(),
        id: "name",
        width: "12%",
        Cell: ({ row }) => (
          <MDTypography
            component="span"
            onClick={() => handleRowClick(row.original)}
            variant="caption"
            color="info"
            fontWeight="medium"
            sx={{
              cursor: 'pointer',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              lineHeight: 1.35,
              display: 'inline-block',
              maxWidth: '100%',
            }}
          >
            {capitalizeFirstLetter(row.original.name)}
          </MDTypography>
        )
      },
      {
        Header: "Account Type",
        accessor: "account_type",
        width: "10%",
        Cell: ({ value }) => (
          <MDTypography variant="caption">
            {capitalizeFirstLetter(value) || "-"}
          </MDTypography>
        ),
      },
      {
        Header: "Partner Type",
        accessor: "partner_type",
        width: "10%",
        Cell: ({ value }) => (
          <MDTypography variant="caption">
            {capitalizeFirstLetter(value) || "-"}
          </MDTypography>
        ),
      },
      {
        Header: "Service Type",
        accessor: "service_type",
        width: "12%",
        Cell: ({ value }) => (
          <MDTypography
            variant="caption"
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              lineHeight: 1.35,
              display: 'inline-block',
              maxWidth: '100%',
            }}
          >
            {value || '-'}
          </MDTypography>
        ),
      },
      {
        Header: "Country",
        accessor: "country",
        width: "10%",
        Cell: ({ value }) => (
          <MDTypography variant="caption">
            {capitalizeFirstLetter(value) || "-"}
          </MDTypography>
        ),
      },
      { 
        Header: "Industry", 
        accessor: "industry", 
        width: "10%",
        Cell: ({ value }) => (
          <MDTypography variant="caption">
            {capitalizeFirstLetter(value) || "-"}
          </MDTypography>
        ),
      },
      { 
        Header: "Account Status", 
        accessor: "account_status", 
        width: "10%",
        Cell: ({ value }) => <StatusBadge status={value} label={capitalizeFirstLetter(value)} />,
      },
      { 
        Header: "Annual Revenue", 
        accessor: "annual_revenue", 
        width: "8%",
        align: "right",
        Cell: ({ value, row }) => (
          <MonoText align="right">
            {value != null && value !== '' ? `${row.original.currency || ''} ${value}`.trim() : '-'}
          </MonoText>
        ),
      },
      { 
        Header: "Currency", 
        accessor: "currency", 
        width: "8%",
        Cell: ({ value }) => <MonoText>{value || '-'}</MonoText>,
      },
      {
        Header: "Owner",
        accessor: "manager_id",
        width: "8%",
        Cell: ({ row }) => {
          const manager = managers.find(manager => manager.id === row.original.manager_id);
          return (
            <MDTypography
              component="span"
              onClick={() => manager && navigate(`/users/${manager.id}/profile`)}
              variant="caption"
              color="info"
              fontWeight="medium"
              sx={{ cursor: 'pointer' }}
            >
              {manager ? capitalizeFirstLetter(manager.fullname) : " "}
            </MDTypography>
          );
        }
      },
      {
        Header: "Modified Date",
        accessor: "updated_at",
        width: "10%",
        Cell: ({ value }) => <MonoText>{value ? formatDate(value) : '-'}</MonoText>,
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "5%",
        align: "center",
        disableSortBy: true,
        Cell: ({ row }) => (
          canDelete ? (
            <TableActionMenu
              row={row.original}
              onView={handleRowClick}
              onDelete={() => handleDeleteClick(row.original)}
            />
          ) : (
            <TableActionMenu
              row={row.original}
              onView={handleRowClick}
            />
          )
        ),
      },

    ],
    rows: filteredAccounts,
    initialState: {
      sortBy: [
        {
          id: 'updated_at',
          desc: true
        }
      ]
    }
  }), [filteredAccounts, managers, canDelete]);



  // Add this function to calculate percentage change

  // Filter accounts by creation date
  const filterAccountsByCreationDate = (accounts, days) => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    return accounts.filter(account => {
      const createdAt = new Date(account.created_at || account.updated_at);
      return createdAt >= cutoffDate;
    });
  };

  // Filter accounts by update date (recently modified)
  const filterAccountsByUpdateDate = (accounts, days) => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    return accounts.filter(account => {
      const updatedAt = new Date(account.updated_at || account.created_at);
      return updatedAt >= cutoffDate;
    });
  };

  const calculateGrowthMetrics = (accounts) => {
    const now = new Date();
    const last30Days = new Date(now);
    last30Days.setDate(now.getDate() - 30);

    // New accounts (created in last 30 days)
    const newAccounts = accounts.filter(account => {
      const createdAt = new Date(account.created_at || account.updated_at);
      return createdAt >= last30Days;
    }).length;

    // Updated accounts (modified in last 30 days)
    const updatedAccounts = accounts.filter(account => {
      const updatedAt = new Date(account.updated_at);
      return updatedAt >= last30Days;
    }).length;

    // Total growth activity (new + updated)
    const totalGrowthActivity = newAccounts + updatedAccounts;

    // Compare with previous period (last 60-90 days)
    const previousPeriodStart = new Date(now);
    previousPeriodStart.setDate(now.getDate() - 90);
    const previousPeriodEnd = new Date(now);
    previousPeriodEnd.setDate(now.getDate() - 30);

    const previousNewAccounts = accounts.filter(account => {
      const createdAt = new Date(account.created_at || account.updated_at);
      return createdAt >= previousPeriodStart && createdAt < previousPeriodEnd;
    }).length;

    const previousUpdatedAccounts = accounts.filter(account => {
      const updatedAt = new Date(account.updated_at);
      return updatedAt >= previousPeriodStart && updatedAt < previousPeriodEnd;
    }).length;

    const previousGrowthActivity = previousNewAccounts + previousUpdatedAccounts;

    // Calculate percentage change
    let growthPercentage = 0;
    if (previousGrowthActivity > 0) {
      growthPercentage = ((totalGrowthActivity - previousGrowthActivity) / previousGrowthActivity) * 100;
    } else if (totalGrowthActivity > 0) {
      growthPercentage = 100; // No previous data, but current growth exists
    }

    return {
      growthPercentage: Math.round(growthPercentage),
      newAccounts,
      updatedAccounts,
    };
  };

  const calculatePercentageChange = (currentCount, previousCount) => {
    if (previousCount === 0) return { amount: "100%", color: "success" }; // handle division by zero
    const change = ((currentCount - previousCount) / previousCount) * 100;
    return {
      amount: `${Math.abs(change).toFixed(1)}%`,
      color: change >= 0 ? "success" : "error"
    };
  };

  // Add this function to filter accounts by time period
  const filterAccountsByPeriod = (accounts, periodDays) => {
    const now = new Date();
    const pastDate = new Date();
    pastDate.setDate(now.getDate() - periodDays);

    return accounts.filter(account => {
      const createdDate = new Date(account.created_at || account.updated_at);
      return createdDate >= pastDate;
    });
  };

  // In your component, calculate the metrics:
  const totalAccounts = accounts.length;
  const activeAccounts = accounts.filter(account => account.account_status === "Active").length;

  // Calculate counts for previous periods (e.g., last month = 30 days)
  const previousMonthAccounts = filterAccountsByPeriod(accounts, 30).length; // accounts created more than 30 days ago
  const previousWeekAccounts = filterAccountsByPeriod(accounts, 7).length; // accounts created more than 7 days ago

  // Calculate percentage changes
  const totalAccountChange = calculatePercentageChange(
    totalAccounts,
    previousMonthAccounts
  );

  const activeAccountChange = calculatePercentageChange(
    activeAccounts,
    previousWeekAccounts
  );

  // For the growth card, you might want to show overall growth
  const allTimeAccounts = accounts.length;
  const lastQuarterAccounts = filterAccountsByPeriod(accounts, 90).length;
  const growthChange = calculatePercentageChange(
    allTimeAccounts,
    lastQuarterAccounts
  );
  const growthMetrics = useMemo(() => calculateGrowthMetrics(accounts), [accounts]);


  // Shared by the classic and v2 layouts below, so both render the same
  // statistics, actions and table.
  const kpiCards = [
    <ComplexStatisticsCard
      key="kpi-0"
      color="dark"
      icon="business"
      title="Total Accounts"
      count={totalAccounts}
      percentage={{
        color: totalAccountChange.color,
        amount: totalAccountChange.amount,
        label: "than last month",
      }}
    />,
    <ComplexStatisticsCard
      key="kpi-1"
      icon="check_circle"
      title="Active Accounts"
      count={activeAccounts}
      percentage={{
        color: activeAccountChange.color,
        amount: activeAccountChange.amount,
        label: "than last week",
      }}
    />,
    <ComplexStatisticsCard
      key="kpi-2"
      color="success"
      icon="leaderboard"
      title="Account Growth"
      count={growthChange.amount}
      percentage={{
        color: growthChange.color,
        amount: growthChange.amount,
        label: "than last quarter",
      }}
    />,
    <ComplexStatisticsCard
      key="kpi-3"
      color="primary"
      icon="person_add"
      title="New Accounts"
      count={filterAccountsByPeriod(accounts, 7).length} // Last 7 days
      percentage={{
        color: "success",
        amount: "10%", // You might calculate this similarly
        label: "than last week",
      }}
    />,
  ];
  const listActions = (
    <>
      <MDButton variant="gradient" color="dark" onClick={handleOpenModal}>
        <Icon sx={{ fontWeight: "bold" }}>add</Icon>
        &nbsp;Add Account
      </MDButton>
    </>
  );
  const listBody = (
    <>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <>
          {filteredAccounts.length > 0 ? (
            <DataTable
              table={accountsTableData}
              isSorted={true}
              entriesPerPage={{
                defaultValue: 10,
                entries: [5, 10, 15, 20, 25],
              }}
              showTotalEntries
              pagination={{ variant: "gradient", color: "info" }}
              noEndBorder
              canSearch
              searchPlaceholder="Search Account / Partner Code"
              exportCSV={true}
              defaultSortField="updated_at"
              defaultSortDirection="desc"
              objectType="account"
              onFilterApply={(conditions) => {}}
              onFilterClear={() => {
                fetchAccounts();
              }}
            />
          ) : (
            <EmptyState
              moduleName="Account"
              // filterType={Accountsfil}
              canCreate={canCreate}
            />
          )}
        </>
      )}
    </>
  );

  const listLabel = app?.labels?.accounts || 'Accounts';
  const v2Main = (
    <MDBox>
      <PageHeader title={listLabel} summary={isLoading ? null : `${filteredAccounts.length} ${listLabel.toLowerCase()}`} actions={listActions} />
      <Grid container spacing={2} mb={2.5}>
        {kpiCards.map((cardEl) => (
          <Grid item xs={12} sm={6} lg={3} key={cardEl.key}>
            {cardEl}
          </Grid>
        ))}
      </Grid>
      <Card>
        <MDBox pt={1}>{listBody}</MDBox>
      </Card>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? v2Main : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {kpiCards[0]}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {kpiCards[1]}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {kpiCards[2]}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {kpiCards[3]}
            </MDBox>
          </Grid>
        </Grid>

        <MDBox mt={4.5}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white">
                    Accounts
                  </MDTypography>
                  {listActions}
                </MDBox>
                <MDBox pt={3}>
                  {listBody}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      )}
      

      <AccountFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveAccount}
        managers={managers}
        currencies={currencies}
        countries={countries}
        loadingCountries={loadingCountries}
        defaultCurrency={defaultCurrency}
        user={user}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteDialog.open}
        onClose={deleteDialog.loading ? undefined : handleDeleteCancel}
        aria-labelledby="delete-account-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 450,
          maxWidth: '90vw',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <Box className="pearl-popup-header" sx={{ backgroundColor: colors.popupHeader.background, px: 4, py: 3 }}>
            <MDTypography id="delete-account-title" variant="h6" component="h2" sx={{ color: '#FFFFFF' }}>
              Delete Account
            </MDTypography>
          </Box>

          <Box sx={{ p: 4 }}>
            <MDTypography variant="body1" mb={3} sx={{ color: '#34495e' }}>
              Are you sure you want to delete{' '}
              <strong>{deleteDialog.account?.name || `Account ${deleteDialog.account?.id ?? ''}`}</strong>?
              This action cannot be undone.
            </MDTypography>

            {deleteDialog.dependencies && deleteDialog.dependencies.has_dependencies ? (
              <Box sx={{
                mb: 3,
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.main',
                bgcolor: 'rgba(255, 193, 7, 0.05)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MDTypography variant="body1" color="warning.dark" fontWeight="medium" sx={{ fontSize: '14px' }}>
                    Dependencies Found ({deleteDialog.dependencies.total_dependencies})
                  </MDTypography>
                </Box>
                <Box component="ul" sx={{ m: 0, pl: 2, mb: 1 }}>
                  {Object.entries(deleteDialog.dependencies.dependencies).map(([key, count]) => (
                    count > 0 && (
                      <li key={key}>
                        <MDTypography variant="caption" color="text">
                          {key.charAt(0).toUpperCase() + key.slice(1)}: {count}
                        </MDTypography>
                      </li>
                    )
                  ))}
                </Box>
                <MDTypography variant="caption" color="error">
                  Please remove or reassign these items before deleting the account.
                </MDTypography>
              </Box>
            ) : null}

            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton
                onClick={handleDeleteCancel}
                variant="outlined"
                color="secondary"
                disabled={deleteDialog.loading}
                sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleDeleteConfirm}
                variant="gradient"
                color="error"
                disabled={
                  deleteDialog.loading ||
                  deleteDialog.checkingDependencies ||
                  deleteDialog.dependencies?.has_dependencies
                }
              >
                {deleteDialog.loading || deleteDialog.checkingDependencies ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  'Delete'
                )}
              </MDButton>
            </MDBox>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </DashboardLayout>
  );
}

export default Accounts;